import { useCallback, useEffect, useState, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SouthWestRoundedIcon from '@mui/icons-material/SouthWestRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import { ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import usePartnerAuth from 'hooks/usePartnerAuth';
import { getTerminalBalance } from 'api/GameApi';

interface Balance {
    sales?: number;
    payout?: number;
    cashIn?: number;
    cashOut?: number;
    cashInHand?: number;
}

const pick = (obj: any, keys: string[]) => {
    for (const k of keys) {
        if (obj && obj[k] != null) return obj[k];
    }
    return undefined;
};
const num = (v: any) => (v == null || v === '' ? undefined : Number(v));

const normalize = (body: any): Balance => ({
    sales: num(pick(body, ['sales', 'Sale', 'totalSale'])),
    payout: num(pick(body, ['payout', 'Payout', 'totalPayout'])),
    cashIn: num(pick(body, ['cashIn', 'cashin', 'CashIn', 'totalCashIn'])),
    cashOut: num(pick(body, ['cashOut', 'cashout', 'CashOut', 'totalCashOut'])),
    cashInHand: num(pick(body, ['cashInHand', 'cashinHand', 'CashInHand', 'cashInHandAmount', 'cashHand', 'balance']))
});

const peso = (n?: number) => `₱${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TerminalBalance = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = usePartnerAuth();

    const [anchor, setAnchor] = useState<null | HTMLElement>(null);
    const [balance, setBalance] = useState<Balance | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchBalance = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const body = await getTerminalBalance({
                username: user.username,
                terminalName: user.terminalName,
                operatorId: user.operatorId,
                enclosureId: user.enclosureId,
                userId: user.userId
            });
            setBalance(normalize(body));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[partner] failed to load terminal balance', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Initial load + on refresh (rehydrated session) once the user is known.
    useEffect(() => {
        fetchBalance();
    }, [fetchBalance]);

    const open = (e: MouseEvent<HTMLElement>) => {
        setAnchor(e.currentTarget);
        fetchBalance(); // refresh details when opening
    };
    const close = () => setAnchor(null);

    const rows: { label: string; value?: number; icon: ReactNode; color: string }[] = [
        { label: 'Sale', value: balance?.sales, icon: <PointOfSaleRoundedIcon sx={{ fontSize: 16 }} />, color: '#5b9cff' },
        { label: 'Payout', value: balance?.payout, icon: <PaymentsRoundedIcon sx={{ fontSize: 16 }} />, color: '#FFBC45' },
        { label: 'Cash In', value: balance?.cashIn, icon: <SouthWestRoundedIcon sx={{ fontSize: 16 }} />, color: '#41A67E' },
        { label: 'Cash Out', value: balance?.cashOut, icon: <NorthEastRoundedIcon sx={{ fontSize: 16 }} />, color: '#e5604d' }
    ];


    return (
        <>
            <ButtonBase
                onClick={open}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: { xs: 0.75, sm: 1.25 },
                    py: 0.5,
                    borderRadius: '999px',
                    border: '1px solid',
                    borderColor: 'rgba(255,188,69,0.5)',
                    background: 'linear-gradient(90deg, rgba(255,188,69,0.18) 0%, rgba(239,86,3,0.18) 100%)',
                    color: '#fff',
                    transition: 'background .2s',
                    '&:hover': {
                        background: 'linear-gradient(90deg, rgba(255,188,69,0.32) 0%, rgba(239,86,3,0.32) 100%)'
                    }
                }}
                aria-label="Cash in hand"
            >
                <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                {loading && !balance ? (
                    <Skeleton width={56} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
                ) : (
                    <Typography
                        component="span"
                        sx={{ fontWeight: 800, fontSize: { xs: '0.75rem', sm: '0.85rem' }, lineHeight: 1, whiteSpace: 'nowrap' }}
                    >
                        {peso(balance?.cashInHand)}
                    </Typography>
                )}
                <ArrowDropDownRoundedIcon sx={{ fontSize: 18, ml: -0.25 }} />
            </ButtonBase>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'center' : 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1.25,
                            width: { xs: 288, sm: 332 },
                            borderRadius: '10px',
                            overflow: 'hidden',
                            background: '#1d222b',
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                            color: '#fff'
                        }
                    }
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        py: 1.75,
                        background: theme.palette.secondary.main,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '8px',
                                display: 'grid',
                                placeItems: 'center',
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(2px)'
                            }}
                        >
                            <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.1 }}>Terminal Balance</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.68rem', opacity: 0.7 }}>Today&apos;s summary</Typography>
                        </Box>
                    </Stack>
                    <IconButton
                        size="small"
                        onClick={fetchBalance}
                        sx={{
                            zIndex: 1,
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.18)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                            '@keyframes tb-spin': { to: { transform: 'rotate(360deg)' } },
                            '& svg': { animation: loading ? 'tb-spin 0.8s linear infinite' : 'none' }
                        }}
                        aria-label="Refresh balance"
                    >
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Stat tiles */}
                <Box sx={{ p: 1.75, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {rows.map((r) => (
                        <Box
                            key={r.label}
                            sx={{
                                p: 1.25,
                                borderRadius: '8px',
                                background: 'rgba(255,255,255,0.035)',
                                border: '1px solid rgba(255,255,255,0.06)'
                            }}
                        >
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '8px',
                                        display: 'grid',
                                        placeItems: 'center',
                                        color: r.color,
                                        background: `${r.color}22`
                                    }}
                                >
                                    {r.icon}
                                </Box>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
                                    {r.label}
                                </Typography>
                            </Stack>
                            {loading ? (
                                <Skeleton width="70%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            ) : (
                                <Typography
                                    sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff', lineHeight: 1.2, overflowWrap: 'anywhere' }}
                                >
                                    {peso(r.value)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Cash In Hand banner */}
                <Box sx={{ px: 1.75, pb: 1.75 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            px: 1.75,
                            py: 1.5,
                            borderRadius: '8px',
                            background: 'rgba(239,86,3,0.12)',
                            border: '1px solid rgba(255,188,69,0.35)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                            <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)' }}>
                                Cash In Hand
                            </Typography>
                        </Stack>
                        {loading ? (
                            <Skeleton width={90} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.15rem',
                                    color: '#FFBC45',
                                    textAlign: 'right',
                                    minWidth: 0,
                                    lineHeight: 1.2,
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {peso(balance?.cashInHand)}
                            </Typography>
                        )}
                    </Stack>
                </Box>
                <Box sx={{ px: 1.75, pb: 1.75 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            px: 1.75,
                            py: 1.5,
                            borderRadius: '8px',
                            background: 'rgba(239,86,3,0.12)',
                            border: '1px solid rgba(255,188,69,0.35)'
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                            <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)' }}>
                                Commission
                            </Typography>
                        </Stack>
                        {loading ? (
                            <Skeleton width={90} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: '1.15rem',
                                    color: '#FFBC45',
                                    textAlign: 'right',
                                    minWidth: 0,
                                    lineHeight: 1.2,
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {balance?.sales != null && <span>{peso(balance.sales * 0.1)}</span>}
                            </Typography>
                        )}
                    </Stack>
                </Box>
            </Popover>
        </>
    );
};

export default TerminalBalance;
